import React, { useState, useEffect, ReactElement, ReactNode } from "react";
import { Box, FormControlLabel, MenuItem, TextField, Checkbox, Divider, Typography, CardMedia, Button, Select } from "@mui/material";
import { Form, FormikErrors, FormikTouched } from "formik";
import { MakeBeacon } from "../../../../types/models/MakeBeacon";
import { BusinessGroup } from "../../../../types/models/BusinessGroup";
import { BeaconDialogModes } from "./BeaconDialog";
import { useAppSelector } from "../../../../utils/hooks";
import FileUploadv2 from "../../../../components/FileUploadv2";
import FileUploadPreview from "../../../../components/FileUploadPreview";
import { useDropzone } from "react-dropzone";
import { AppColor } from "../../../../types/Constants";
import AppConfirmDialog from "../../../../components/AppConfirmDialog";
import { getImageURLCategoryListIcon, getImageURLOnServerBeaconFace } from "../../../../utils/utils";
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import BeaconCategory from "../../../../components/BeaconCategory";
import CategoryDropdown from "../../pages/categories/CategoryDropdown";
import { Category } from "../../../../types/models/Category";
import HorizontalMenu from "./HorizontalMenu";
import ImageLoader from "../../../../components/ImageLoader";

interface BeaconFormProps {
  mode?: typeof BeaconDialogModes[number],
  values: MakeBeacon,
  errors: FormikErrors<MakeBeacon>,
  touched: FormikTouched<MakeBeacon>,
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => Promise<void | FormikErrors<MakeBeacon>>
  groups: Array<BusinessGroup>
  imageId2?: string,
  handleChange: (e: React.ChangeEvent<any>) => void,
  handleDeleteFace: (deleteFaceImage: boolean) => void,
  handleFileChange2: (file: File | undefined) => void,
  onCategorySelect: (category: Category) => void;
}

const BeaconForm: React.FC<BeaconFormProps> = ({ mode, values, errors, handleChange, touched, setFieldValue, groups, imageId2, handleFileChange2, handleDeleteFace, onCategorySelect }) => {

  const categoriesGroupState = useAppSelector((state) => state.category);

  const [uploadedFileForScreen2, setUploadedFileForScreen2] = useState<File | undefined>(undefined);
  const [tempImageURL, setTempImageURL] = useState<string>("");
  const [openFileDimensionsExceededMessage, setOpenFileDimensionsExceededMessage] = useState<boolean>(false);
  const [showDistance, setShowDistance] = useState<boolean>(values.show_distance ?? true);
  const [deleteFaceImage, setDeleteFaceImage] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const dropzoneForScreen2 = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg'],
    },
    maxFiles: 1,
  });

  useEffect(() => {

    setFieldValue("show_distance", true);
    
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (categoriesGroupState) {
      const category = categoriesGroupState.categories?.find(
        (cat) => cat.Name === values.category);
      if (category) {
        setSelectedCategory(category);
        onCategorySelect(category);
      }
    }

  }, [categoriesGroupState]);

  useEffect(() => {
    if (dropzoneForScreen2.acceptedFiles && dropzoneForScreen2.acceptedFiles.length >= 1) {
      const file = dropzoneForScreen2.acceptedFiles[0];
      // Check image dimensions to make sure its 256 x 256
      getImageDimensions(file, (width, height) => {
        if (width == 256 && height == 256) {
          const imageUrl = URL.createObjectURL(file);
          setUploadedFileForScreen2(file);
          handleFileChange2(file);
          setTempImageURL(imageUrl);
        }
        else {
          setOpenFileDimensionsExceededMessage(true);
        }
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dropzoneForScreen2.acceptedFiles]);

  const getImageDimensions = (file: File, callback: (width: number, height: number) => void) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        callback(img.width, img.height);
      };
      img.src = e.target?.result as string;
    };
    reader.readAsDataURL(file);
  };

  const onDeleteUploadFileForScreen = () => {
    dropzoneForScreen2.acceptedFiles.splice(0,1);
    setUploadedFileForScreen2(undefined);
    handleFileChange2(undefined);
    setDeleteFaceImage(true);
    setTempImageURL("");
    handleDeleteFace(true);
  };

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputFile = event.target.files![0];

    getImageDimensions(inputFile, (width, height) => {
      if (width == 256 && height == 256) {
        const imageUrl = URL.createObjectURL(inputFile);
        setUploadedFileForScreen2(inputFile);
        handleFileChange2(inputFile);
        setTempImageURL(imageUrl);
      } else {
        setOpenFileDimensionsExceededMessage(true);
      }
    });
    event.target.value = "";
  };  

  const handleDistanceCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setShowDistance(checked);
    setFieldValue("show_distance", checked);
    handleChange(event);
  };

  const handleCategorySelect = (category: Category) => {
    setSelectedCategory(category);
    onCategorySelect(category);
    setFieldValue("category", category.Name);
  };

  const handleModelSelect = (model: string) => {
    setFieldValue("model", model);
  };

  return (
    <Form style={{ width: "100%", marginBottom: 8, marginTop: 8 }}>
      <Box sx={{ mr: 1 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 2 }}>Category</Typography>
        {/* <TextField
          disabled={mode === "MOD_ONLY" || mode === "READ_ONLY"}
          sx={{ padding: 2, marginTop: 1 }}
          required
          fullWidth
          variant="outlined"
          name="category"
          id="category"
          select
          value={values.category ?? ''}
          error={((errors.category ? true : undefined) && touched.category)}
          onChange={handleCategoryChange}
          helperText={errors.category && touched.category ? errors.category : null}>
          {getCategoryMenuItems()}
        </TextField> */}
        <CategoryDropdown 
          onCategorySelect={handleCategorySelect}
          errors={errors}
          touched={touched}
          initalCategory={selectedCategory}
        />
        {/* <Divider sx={{ my: 4 }} />
        <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Beacon Type</Typography>
        <Box sx={{ display: "flex", gap: 10 }}>
        <HorizontalMenu 
          onModelSelect={handleModelSelect}
          model={values.model}
        />
        <FormControlLabel control={<Checkbox checked={showDistance} onChange={handleDistanceCheckboxChange}/>} label="Display Distance" />
        </Box> */}
        <Divider sx={{ my: 4 }} />  
        <Box sx={{marginTop: 6}}>
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>Icon</Typography>
          <Box sx={{ display: "flex", gap: 10, marginTop: 1, padding: 2 }}>
              <Box
                sx={{
                  border: 'dashed 2px silver',
                  borderRadius: 2.5,
                  p: 3,
                  textAlign: 'center',
                  color: 'text.secondary',
                  backgroundColor: `#${selectedCategory?.Color}`,
                  alignContent: 'center',
                  width: 128,
                  height: 128
                }}
              >
                {imageId2 || tempImageURL ? (
                    <CardMedia
                    sx={{ width: "100%", height: "100%", objectFit: "cover", overflow: "hidden" }}
                    component="img"
                    src={tempImageURL ? tempImageURL : getImageURLOnServerBeaconFace(imageId2 ?? "", "")}
                    alt="beacon"
                  />
                ) : (
                  <>
                  {selectedCategory?.Id ? (
                  <Box sx={{ alignContent: "center", justifyContent: "center", display: "flex", alignItems: "center" }}>
                    <ImageLoader 
                      imageURL={getImageURLCategoryListIcon(selectedCategory.Id, selectedCategory.LastModified)}
                    />
                  </Box>
                  ) : (
                    <ImageOutlinedIcon
                    style={{
                      fontSize: 40,
                      color: 'silver',
                    }}
                  />
                )}
                </>
                )}
            </Box>
            <Box sx={{ display: "flex", alignItems: "end", mb: 1 }}>
            <label htmlFor="file-input-face">
            <Button
              component="span"
              variant="outlined"
              sx={{
                whiteSpace: "nowrap",
                height: 40,
              }}>
                Upload Custom Image
            </Button>
            </label>
            <input
              id="file-input-face"
              type="file"
              accept=".png,.jpg,jpeg"
              style={{ display: 'none' }}
              onChange={handleFilterChange}
            />
            </Box>
          </Box>
          {(uploadedFileForScreen2 || (!deleteFaceImage && imageId2 != undefined)) &&
            <Box sx={{ paddingX: 2 }}>
              <FileUploadPreview
                file={uploadedFileForScreen2}
                imageId={imageId2}
                onDeleteUploadFile={onDeleteUploadFileForScreen}
              />
            </Box>}
        </Box>
      </Box>
      <AppConfirmDialog
          open={openFileDimensionsExceededMessage}
          onConfirm={() => setOpenFileDimensionsExceededMessage(false)}
          confirmButtonTitle="OK"
          disableDenyButton={true}
          title={`The image is not 256x256. Please try resizing the image then uploading it again.`}
          dialogTitle="Problem attaching image to beacon"
          content=""
        />
    </Form>
  );
};

export default BeaconForm;
